export type PdfReduceResult = { file: File; notice: string | null };

type Args = {
  file: File;
  maxBytes: number;
  label: string;
  formatBytes: (bytes: number) => string;
};

const getPdfWorkerSrc = () => {
  const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${publicUrl}/pdf.worker.min.mjs`;
};

export async function optimizePdfToMaxBytes(args: Args): Promise<PdfReduceResult> {
  let { file } = args;
  const { maxBytes, label, formatBytes } = args;

  if (Number(file.size || 0) <= maxBytes) return { file, notice: null };

  try {
    const { PDFDocument } = await import('pdf-lib');
    const inputBytes = new Uint8Array(await file.arrayBuffer());
    const pdf = await PDFDocument.load(inputBytes, { ignoreEncryption: true, updateMetadata: false });
    const outputBytes = await pdf.save({
      useObjectStreams: true,
      addDefaultPage: false,
      updateFieldAppearances: false,
      objectsPerTick: 50,
    });
    const compressedFile = new File([outputBytes], file.name, {
      type: file.type || 'application/pdf',
      lastModified: Date.now(),
    });
    if (compressedFile.size < file.size) {
      if (compressedFile.size <= maxBytes) {
        return {
          file: compressedFile,
          notice: `${label}: PDF optimized (${formatBytes(file.size)} → ${formatBytes(compressedFile.size)}).`,
        };
      }
      file = compressedFile;
    }
  } catch (err: any) {
    return {
      file,
      notice: `${label}: auto-compress failed (${String(err?.message || 'unknown error')}).`,
    };
  }

  try {
    const runRasterCompression = async (scale: number, quality: number): Promise<File | null> => {
      if (typeof document === 'undefined') return null;
      const pdfjsLib: any = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const { jsPDF } = await import('jspdf');
      if (pdfjsLib?.GlobalWorkerOptions) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc();
      }
      const inputBytes = new Uint8Array(await file.arrayBuffer());
      const loadingTask = pdfjsLib.getDocument({
        data: inputBytes,
        disableWorker: true,
      });
      const sourceDoc = await loadingTask.promise;
      const pageCount = Number(sourceDoc?.numPages || 0) || 0;
      let out: any = null;
      for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
        const page = await sourceDoc.getPage(pageIndex);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        const context = canvas.getContext('2d');
        if (!context) continue;
        await page.render({ canvasContext: context, viewport }).promise;
        const imageData = canvas.toDataURL('image/jpeg', quality);
        const orientation = viewport.width > viewport.height ? 'l' : 'p';
        if (!out) {
          out = new jsPDF({
            orientation,
            unit: 'pt',
            format: [viewport.width, viewport.height],
            compress: true,
          });
        } else {
          out.addPage([viewport.width, viewport.height], orientation);
        }
        out.addImage(imageData, 'JPEG', 0, 0, viewport.width, viewport.height, undefined, 'FAST');
        canvas.width = 1;
        canvas.height = 1;
      }
      if (!out) return null;
      const blob = out.output('blob');
      if (!blob) return null;
      return new File([blob], file.name, {
        type: file.type || 'application/pdf',
        lastModified: Date.now(),
      });
    };

    const attempts: Array<{ scale: number; quality: number }> = [
      { scale: 1.2, quality: 0.68 },
      { scale: 1.0, quality: 0.58 },
      { scale: 0.9, quality: 0.5 },
      { scale: 0.8, quality: 0.42 },
    ];
    let best = file;
    for (const attempt of attempts) {
      const candidate = await runRasterCompression(attempt.scale, attempt.quality);
      if (!candidate) continue;
      if (candidate.size < best.size) best = candidate;
      if (candidate.size <= maxBytes) {
        return {
          file: candidate,
          notice: `${label}: PDF optimized (${formatBytes(file.size)} → ${formatBytes(candidate.size)}).`,
        };
      }
    }
    if (best.size < file.size) {
      return {
        file: best,
        notice: `${label}: PDF optimized (${formatBytes(file.size)} → ${formatBytes(best.size)}), but still above limit.`,
      };
    }
    return { file, notice: `${label}: PDF optimization did not reduce file size.` };
  } catch (rasterErr: any) {
    return {
      file,
      notice: `${label}: auto-compress failed (${String(rasterErr?.message || 'unknown error')}).`,
    };
  }
}
